import { BrButton, BrDivider, Col, Row } from '@govbr-ds/react-components';
import InfoCircleTooltip from '../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import { IOperacao } from '../../../../../../../interfaces/IOperacaoPLG';
import styles from './titularArrendatarioOperacaoFormReadOnly.module.scss';
import { formatarNrProcesso } from '../../../../../../../utils/formatNrProcesso';
import { IProcessoMinerario } from '../../../../../../../interfaces/IProcessos';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from '../../../../../lib/backEndRequests';
import { useEffect } from 'react';
import { ETipoOperacao, OperacaoDetalhe } from '../../../../../../../enums/ETipoOperacao';
import { formatarDataISOpBR } from '../../../../../../../utils/formatDate';
import { formatNumber, removeNumberFormat } from '../../../../../../../utils/formatNumber';

interface ITitularArrendatarioOperacaoFormReadOnlyProps {
  operacao: IOperacao | undefined;
  setStepFormulario: React.Dispatch<React.SetStateAction<number>>;
  setIsEdicaoCabecalho: React.Dispatch<React.SetStateAction<boolean>>;
  setUnidadesMedida: React.Dispatch<
    React.SetStateAction<
      {
        label: string;
        value: string;
      }[]
    >
  >;
  unidadesMedida: {
    label: string;
    value: string;
  }[];
}

function TitularArrendatarioOperacaoFormReadOnly({
  operacao,
  setStepFormulario,
  setIsEdicaoCabecalho,
  setUnidadesMedida,
  unidadesMedida,
}: Readonly<ITitularArrendatarioOperacaoFormReadOnlyProps>) {
  const { data: processoData } = useQuery<IProcessoMinerario, IErrorWithSentryId>({
    queryKey: ['processoPLGQuery', operacao?.numeroProcesso],
    queryFn: () => getProcessoPLG(operacao?.numeroProcesso || ''),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: unidadesMedida.length === 0,
  });

  const { getProcessoPLG } = useBackEndRequests();

  const editarCabecalho = () => {
    setStepFormulario(1);
    setIsEdicaoCabecalho(true);
  };

  useEffect(() => {
    if (processoData && unidadesMedida.length === 0) {
      const substancia = processoData.substancias.filter((substancia) => substancia.nome === operacao?.substancia)[0];

      if (substancia) {
        const unidadesMedida = substancia.unidadesMedida.map((unidade) => ({
          label: unidade,
          value: unidade,
        }));
        setUnidadesMedida(unidadesMedida);
      }
    }
  }, [processoData]);

  return (
    <div className={styles.container}>
      <Row className={styles.row}>
        <Col className={styles.dadosCol}>
          <span className={styles.label}>Nº do Processo Minerário</span>
          <span className={styles.dadoAdicionado}>
            {operacao?.numeroProcesso ? formatarNrProcesso(operacao.numeroProcesso) : ''}
          </span>
        </Col>
        <Col className={styles.dadosCol}>
          <span className={styles.label}>Município/UF</span>
          <span className={styles.dadoAdicionado}>
            {operacao?.municipio?.nome}/{operacao?.municipio?.uf}
          </span>
        </Col>
        <Col className={styles.dadosCol}>
          <span className={styles.label}>Substância</span>
          <span className={styles.dadoAdicionado}>{operacao?.substancia}</span>
        </Col>
      </Row>
      <Row className={styles.row}>
        <Col className={styles.dadosCol}>
          <span className={styles.label}>Uni. de Medida</span>
          <span className={styles.dadoAdicionado}>{operacao?.unidadeMedida}</span>
        </Col>
        <Col className={styles.dadosCol}>
          <span className={styles.label}>Tipo de Vínculo</span>
          <span className={styles.dadoAdicionado}>
            {operacao?.tipoVinculo === 'TITULAR' ? 'Titular' : 'Arrendatário'}
          </span>
        </Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row className={styles.rowProducao}>
        <Col className={styles.dadosCol}>
          <div className={styles.checkBoxLabelContainer}>
            <div className={styles.checkBoxLabel}>
              <InfoCircleTooltip
                text='Caracteriza se houve ou não produção bruta e/ou beneficiada no período de apuração declarado.'
                id='producao'
              />
              <span className={styles.label}>Houve Produção de Bem Mineral?</span>
            </div>
          </div>
        </Col>
        <Col className={styles.checkbox}>
          <label className={styles.labelSwitch}>{operacao?.hasProducao ? 'Sim' : 'Não'}</label>
        </Col>
        <Col className={styles.dadosCol}>
          {operacao?.hasProducao && (
            <>
              <label className={styles.label}>Quantidade Produzida</label>
              <span className={styles.dadoAdicionado}>{formatNumber(operacao?.quantidadeProduzida)}</span>
            </>
          )}
        </Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row className={styles.row}>
        <Col className={styles.dadosCol}>
          <div className={styles.checkBoxLabelContainer}>
            <div className={styles.checkBoxLabel}>
              <InfoCircleTooltip
                text='Caracteriza se houve ou não fato gerador da CFEM para o período de apuração declarado.'
                id='movimentacao'
              />
              <span>Houve Fato Gerador da CFEM?</span>
            </div>
          </div>
        </Col>
        <Col className={styles.checkbox}>
          <label className={styles.labelSwitch}>{operacao?.hasFatoGerador ? 'Sim' : 'Não'}</label>
        </Col>
        <Col></Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row className={styles.row}>
        <Col className={styles.dadosCol}>
          <div className={styles.checkBoxLabelContainer}>
            <div className={styles.checkBoxLabel}>
              <InfoCircleTooltip
                text='As pessoas físicas e jurídicas que tenham, em caráter permanente ou eventual, como atividade principal ou acessória, cumulativamente ou não, a compra e venda de moeda estrangeira ou ouro como ativo financeiro ou instrumento cambial, deverão comunicar as autoridades competentes, suas operações financeiras, dispensando especial atenção às operações que, nos termos de instruções emanadas das autoridades competentes, possam constituir-se em sérios indícios dos crimes previstos na Lei nº 9.613, de 03 de março de 1998.'
                id='irregularidades'
              />
              Há indicativo de irregularidade neste processo minerário?
            </div>
          </div>
        </Col>
        <Col className={styles.checkbox}>
          <label className={styles.labelSwitch}>{operacao?.hasSuspeitaIrregularidade ? 'Sim' : 'Não'}</label>
        </Col>
        <Col className={styles.dadosCol}>
          {operacao?.hasSuspeitaIrregularidade && (
            <>
              <span className={styles.label}>Irregularidade encontrada</span>
              <span className={styles.dadoAdicionado}>{operacao?.descricaoSuspeitaIrregularidade}</span>
            </>
          )}
        </Col>
      </Row>
      {operacao?.observacoes && (
        <>
          <div className={styles.dividerContainer}>
            <BrDivider />
          </div>
          <Row className={styles.row}>
            <Col className={`col-4`}>
              <span className={styles.textAreaLabel}>
                <InfoCircleTooltip
                  text='Outras observações pertinentes a quaisquer aquisições que envolvem a tríade processo minerário, substância e município preenchida acima.'
                  id='observacoes'
                />
                Observações
              </span>
            </Col>
            <Col>
              <div className={styles.observacoesContainer}>
                <span className={styles.dadoAdicionado}>{operacao?.observacoes}</span>
              </div>
            </Col>
          </Row>
        </>
      )}
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row className={styles.row}>
        <Col className={styles.dadosCol}>
          <div className={styles.checkBoxLabel}>
            <InfoCircleTooltip
              text='Caso a Substância selecionada seja DIFERENTE de "Ouro" e haja movimentação/produção, o campo Tipo da Operação será desabilitado com opção "Venda direta ao Primeiro Adquirente" selecionada.'
              id='tipoOperacao'
            />
            <span className={styles.label}>Tipo da Operação</span>
          </div>
        </Col>
        <Col>
          <span className={styles.dadoAdicionado}>
            {OperacaoDetalhe[ETipoOperacao[operacao?.tipoOperacao || 'VENDA_DIRETA_PRIMEIRO_ADQUIRENTE']]?.label || '-'}
          </span>
        </Col>
        <Col></Col>
      </Row>
      <div className={styles.editButton}>
        <BrButton circle icon='fas fa-edit fa-md' secondary onClick={() => editarCabecalho()} />
      </div>
    </div>
  );
}
export default TitularArrendatarioOperacaoFormReadOnly;
